import { Navigate, useLocation, useParams } from 'react-router-dom';

import { Loader } from '@rikstv/shared-components';

import { AssetNotFound } from '../components/errorPage/AssetNotFound';
import { AssetDetails } from '../forces/assetDetails/assetDetails.types';
import { useAssetDetails } from '../forces/assetDetails/useAssetDetails';
import { TvSeriesDetails } from '../forces/tvSeries/tvSeriesDetails.types';
import { useTvSeries } from '../forces/tvSeries/useTvSeries';
import { useReportUnlessNotFound } from '../hooks/useReportUnlessNotFound';
import { getStackContent } from '../pages/StackedCoverPage/hooks/scp.utils';
import { useFetchSuperCoverPage } from '../pages/StackedCoverPage/hooks/useFetchSuperCoverPage';

import { commonRoutes } from './router.path';

type DeepLinkTypes = 'samling' | 'serie' | 'program' | 'kanal';

export const deepLinkPathPattern = '/se/:type/:id';

export const DeepLinkRedirector = () => {
  const { type, id } = useParams<{ type: DeepLinkTypes; id: string }>();
  const location = useLocation();

  if (type && id) {
    switch (type) {
      case 'kanal':
        return <Navigate to={commonRoutes.watchLive(id, undefined) + location.search} replace />;
      case 'program':
        return <ProgramRedirector id={id} search={location.search} />;
      case 'samling':
        return <Navigate to={commonRoutes.collections({ slug: id }) + location.search} replace />;
      case 'serie':
        return <SeriesRedirector seriesId={id} search={location.search} />;
    }
  }
  // fallback redirect
  return <Navigate to={'/' + location.search} />;
};

const SeriesRedirector = ({ seriesId, search }: { seriesId: string; search: string }) => {
  // Attempt to get super cover page first and fall back to assuming id is an seriesId
  const { scpLink, error: superError, isLoading: superLoading } = useTryGetSuperCoverPageLink(seriesId);
  const { data, error, isLoading } = useTvSeries(superError != null ? seriesId : undefined);
  // superError is not reported: a cover page miss is the expected signal to fall back
  useReportUnlessNotFound(error);
  if (scpLink) {
    return <Navigate to={scpLink + search} replace />;
  }
  if (error) {
    return <AssetNotFound />;
  }
  if (superLoading || isLoading) {
    return <Loader size="large" />;
  }
  if (data) {
    const link = getSuperCoverPageLink(data) ?? commonRoutes.watchTvSeries(seriesId);
    return <Navigate to={link + search} replace />;
  }
  return null;
};

const ProgramRedirector = ({ id, search }: { id: string; search: string }) => {
  // Attempt to get super cover page first and fall back to assuming id is an assetId
  const { scpLink, error: superError, isLoading: superLoading } = useTryGetSuperCoverPageLink(id);
  const { data, error, isLoading } = useAssetDetails(superError != null ? id : undefined);
  // superError is not reported: a cover page miss is the expected signal to fall back
  useReportUnlessNotFound(error);
  if (scpLink) {
    return <Navigate to={scpLink + search} replace />;
  }
  if (error) {
    return <AssetNotFound />;
  }
  if (superLoading || isLoading) {
    return <Loader size="large" />;
  }
  if (data) {
    const redirectLink = getSuperCoverPageLink(data) ?? commonRoutes.watchAsset({ id });
    return <Navigate to={redirectLink + search} replace />;
  }
  return null;
};

export const getSuperCoverPageLink = (data: AssetDetails | TvSeriesDetails) => {
  if ('links' in data && data.links.superCoverPage) {
    return commonRoutes.stackedSeriesOrMovie({
      id: data.links.superCoverPage.id,
      title: data.name,
      isSeries: data.links.seriesUrl != null,
    });
  } else if ('episodeCount' in data) {
    try {
      const superId = data.seasons[0]?.episodes[0]?._links.superCoverPage?.id;
      if (superId) {
        const superSeriesId = superId.split(':').slice(0, 2).join(':');
        return commonRoutes.stackedSeriesOrMovie({ id: superSeriesId, isSeries: true, title: data.name });
      }
    } catch {
      /* om nom nom */
    }
  }

  return null;
};

const BASE_PATH = window.location.origin;

export const deepLinkRoutes = {
  program: (programId: string, absoluteUrl = false) =>
    `${absoluteUrl ? BASE_PATH : ''}/se/program/${programId}` as const,
  series: (seriesId: string, absoluteUrl = false) => `${absoluteUrl ? BASE_PATH : ''}/se/serie/${seriesId}` as const,
  collection: (collectionSlug: string, absoluteUrl = false) =>
    `${absoluteUrl ? BASE_PATH : ''}/se/samling/${collectionSlug}` as const,
  liveChannel: (channelId: string, absoluteUrl = false) =>
    `${absoluteUrl ? BASE_PATH : ''}/se/kanal/${channelId}` as const,
};

/**
 * These prefixes are set by the API in InterAppShareLinks
 * and are used to identify/differentiate superId's from assetId's
 */
const ContentStackIdPrefix = 'cs_';
const useTryGetSuperCoverPageLink = (superId: string) => {
  /**
   * If an id contains a colon it is probably a super id
   * If an id start with "cs_" it is definitively a super id
   * Example id´s: 12345678:vod, 12345678:linear
   */
  const enableUseSuperIds = superId.startsWith(ContentStackIdPrefix) || superId.includes(':');
  const id = enableUseSuperIds ? superId.replace(ContentStackIdPrefix, '') : null;
  const { data, error, isLoading } = useFetchSuperCoverPage({ id });

  let superCoverPageLink = null;
  if (data) {
    const stack = getStackContent(data);
    superCoverPageLink = commonRoutes.stackedSeriesOrMovie({
      id: data.id,
      title: stack.metadata.title,
      isSeries: data.type === 'series',
    });
  }

  if (enableUseSuperIds === true) {
    return { error, isLoading, scpLink: superCoverPageLink };
  }

  return { error: new Error(), isLoading: false, scpLink: null };
};
